import React, { useRef, useState } from 'react'
import { ShoesState } from 'src/types'
import { useLocale } from 'src/locales/useLocale'
import { useConfirm } from 'src/hooks/useConfirm'
import { ConfirmDialog } from 'src/Components/Ui/ConfirmDialog'
import { generateSubject } from 'src/logic'
import { saveShoesSimulation, sendUnifiedMail } from 'src/lib/api-client'
import { canvasToCompressedImage } from 'src/util/canvas'

type Images = Pick<ShoesState, 'images'>['images']

type Props = {
  state: ShoesState
  disabled: boolean
  email: string
  frontSurfaceId: string
  sideSurfaceId: string
  rearSurfaceId: string
  setSavedId: React.Dispatch<React.SetStateAction<string>>
  setIsSave: React.Dispatch<React.SetStateAction<boolean>>
}

export const ShoesSaveButton: React.FC<Props> = ({ state, disabled, email, frontSurfaceId, sideSurfaceId, rearSurfaceId, setSavedId, setIsSave }) => {
  const { t, locale, isJa, isEn, isZhTw, isKo } = useLocale()
  const [isSaving, setIsSaving] = useState(false)
  const executeRef = useRef(false)
  const { isConfirmOpen, handleConfirmOpen, handleConfirmClose } = useConfirm()

  const captureImages = (): Images => ({
    front: canvasToCompressedImage(frontSurfaceId),
    side: canvasToCompressedImage(sideSurfaceId),
    rear: canvasToCompressedImage(rearSurfaceId)
  })
  const handleSave = async (state: ShoesState, newImages: Images) => {
    handleConfirmClose()
    setIsSaving(true)
    if (executeRef.current) return
    try {
      executeRef.current = true
      const newState = { ...state, images: newImages }
      const response = await saveShoesSimulation(newState)
      if (response.id === '999') return

      const savedId = response.id
      setSavedId(savedId)

      // メール送信は非同期で実行（エラーが発生してもUIには影響しない）
      sendUnifiedMail({
        subject: generateSubject(savedId, 'atoms', isJa, isEn, isZhTw, isKo),
        savedId,
        imageUrls: newImages,
        email,
        locale
      }).catch((error) => {
        console.error('メール送信に失敗しました:', error)
      })

      setIsSaving(false)
      executeRef.current = false
    } catch (err) {
      executeRef.current = false
      console.error('シューズシミュレーション保存エラー:', err)
    }
    handleConfirmClose()
    setIsSave(true)
  }

  return (
    <ConfirmDialog
      isConfirmOpen={isConfirmOpen}
      openButtonLabel={t.SAVE_BUTTON}
      dialogMessage={t.CONFIRM_SEND_EMAIL}
      color={'primary'}
      variant={'contained'}
      disabled={disabled || isSaving}
      isLoading={isSaving}
      handleOkButton={handleConfirmOpen}
      handleCancelButton={handleConfirmClose}
      handleExecuteButton={() => handleSave(state, captureImages())}
    />
  )
}
